import React, { useCallback, useEffect } from 'react';
import Events from '../../../events';
import { EventsType } from '../../../events-types';
import { deleteDoc, getObjectsByRegEx, save, TOXICANTS_DISEASE } from '../../../api/Compounds';
import { iBaseCardWithComponentProps, iBaseInnerCardProps } from "../../types";
import { CardWithBaseComponent } from "../../common/BaseCard";
import { isNotEmpty } from "../../../utils/string";
import FormEditModal from "../../common/FormEditModal";
import { renderFields } from "../../../utils/pair_entry";
import AuditModal from "../../common/AuditModal";
import DisplayDeleteModal from "../../common/DeleteConfirmationModal";
import { useRoles } from "../../../App.Context";
import { ToxicantsDisease } from "./types";

const _ToxicantsDiseasesCard: React.FC<iBaseInnerCardProps> = ({
                                                                   setIsShow,
                                                                   setError,
                                                                   setData,
                                                                   data,
                                                                   isActive,
                                                                   setTitle,
                                                                   setSubtitle,
                                                                   searchBy,
                                                                   searchByDoc
                                                               }) => {
    const UUID = '_ToxicantsDiseasesCard';
    const roles = useRoles();
    const hasReadOnlyRole = roles.includes("ReadOnly");

    // MODAL EDIT CALLBACK
    const saveData = useCallback(async (data: any) => {
        save(TOXICANTS_DISEASE, data).then((res) => {
            setData(res);
        }).catch(setError);
    }, [setData, setError]);

    const deleteData = async (data: any) => {
        deleteDoc(TOXICANTS_DISEASE, data).then((res) => {
            setData({});
            setIsShow(false);
        }).catch(setError);
    };

    const undo = async (undoData: any) => {
        Object.assign(data, undoData);
        save(TOXICANTS_DISEASE, data).then((res) => {
            setData(res);
        }).catch(setError);
    };

    const createEvent = useCallback((event: { detail: any }) => {
        if (isNotEmpty(event.detail)) {
            delete event.detail._id;
            saveData(event.detail);
        }
    }, [saveData]);

    // SEARCH ------------------------

    const _search = useCallback((text: string) => {
        if (text.length > 0) {
            searchBy?.(getObjectsByRegEx, TOXICANTS_DISEASE, 'INCHIKEY,DISEASE_CATEGORY,COMMENT', text);
        }
    }, [searchBy]);

    const searchByInchiKeyEvent = useCallback((event: { detail: string }) => {
        if (event.detail.length > 0 && data.INCHIKEY !== event.detail) {
            searchBy?.(getObjectsByRegEx, TOXICANTS_DISEASE, 'INCHIKEY', '^' + event.detail + '$');
        }
    }, [searchBy, data]);

    const searchEvent = useCallback(async (event: { detail: string }) => {
        _search(event.detail);
    }, [_search]);

    const searchByCompoundEvent = useCallback(async (event: { detail: any }) => {
        setData(event.detail);
        setIsShow(true);
    }, [setData, setIsShow]);

    // ----- EVENTS

    useEffect(() => {
        Events.on(EventsType.CREATE, createEvent, UUID);
        if (isActive) {
            Events.on(EventsType.SEARCH, searchEvent, UUID);
            Events.on(EventsType.SEARCH_BY_INCHIKEY, searchByInchiKeyEvent, UUID);
            Events.on(EventsType.SEARCH_BY_TOXICANTS_DISEASE, searchByCompoundEvent, UUID);
        }

        return () => {
            Events.off(EventsType.CREATE, UUID);
            Events.off(EventsType.SEARCH, UUID);
            Events.off(EventsType.SEARCH_BY_INCHIKEY, UUID);
            Events.off(EventsType.SEARCH_BY_TOXICANTS_DISEASE, UUID);
        };
    }, [isActive, createEvent, searchEvent, searchByInchiKeyEvent, searchByCompoundEvent]);

    // RENDERS --------------

    return (
        <p>
            {data.COMPOUND_NAME && (
                <React.Fragment>
                    <b>COMPOUND NAME</b>: {data.COMPOUND_NAME}
                    <br />
                </React.Fragment>
            )}
            {data.CAS && (
                <React.Fragment>
                    <b>CAS</b>: {data.CAS}
                    <br />
                </React.Fragment>
            )}
            {renderFields(ToxicantsDisease, data)}

            { !hasReadOnlyRole && (
                <>
                    <FormEditModal
                        save={saveData}
                        data={data}
                        schema={ToxicantsDisease}
                        excludeListParameters={[]}/>
                    <DisplayDeleteModal
                        deleteDoc={deleteData}
                        data={data}
                        schema={ToxicantsDisease}/>
                </>
            )}

            <AuditModal id={data._id} undo={undo}/>
        </p>
    );
};

const ToxicantsDiseasesCard: React.FC<Omit<iBaseCardWithComponentProps, 'cardComponent'>> = ({ header, width, eventGroupToTrigger }) => {
    return <CardWithBaseComponent cardComponent={_ToxicantsDiseasesCard} header={header} width={width} eventGroupToTrigger={eventGroupToTrigger}/>;
};

export default React.memo(ToxicantsDiseasesCard);
